import { brandIdentity, brandPublishingAreas } from '@/mocks/home';
import { clamp, TRANSITION, COLORS } from './progress';

interface BrandSceneProps {
  progress: number;
}

export default function BrandScene({ progress }: BrandSceneProps) {
  // 進場淡入 + 上移，出場淡出
  const p = clamp(progress, 0, 1);
  const enter = clamp(p * 3, 0, 1);
  const exit = 1 - clamp((p - 0.82) * 5.5, 0, 1);
  const opacity = Math.min(enter, exit);
  const translateY = (1 - enter) * 60;

  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center px-5 md:px-8 pt-20 md:pt-24 pb-10 pointer-events-none"
      style={{
        opacity,
        transform: `translateY(${translateY}px)`,
        transition: TRANSITION,
        willChange: 'transform, opacity',
      }}
    >
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-6">
        {/* 左側：品牌身分（米白皂塊面板） */}
        <div
          className="lg:col-span-2 flex flex-col justify-between p-6 md:p-8"
          style={{ backgroundColor: COLORS.cream, color: COLORS.ink }}
        >
          <div>
            <div className="flex items-center gap-2 mb-6">
              <span className="w-1.5 h-1.5" style={{ backgroundColor: COLORS.ink }} />
              <span className="font-manrope text-[10px] uppercase tracking-[0.32em] text-[#0B1B3A]/70">
                Vol. 01 — 品牌資訊
              </span>
            </div>

            <h2
              className="font-michroma uppercase leading-[0.95] mb-6"
              style={{ fontSize: 'clamp(24px, 3.2vw, 44px)', letterSpacing: '-0.02em' }}
            >
              {brandIdentity.englishName}
            </h2>

            <p className="font-semibold text-xl md:text-2xl mb-1">
              {brandIdentity.brandName}
            </p>
            <p className="font-manrope text-xs text-[#0B1B3A]/60 mb-6">
              {brandIdentity.fullCompanyName}
            </p>

            <div className="h-px w-full mb-6" style={{ backgroundImage: 'repeating-linear-gradient(to right, #0B1B3A 0, #0B1B3A 2px, transparent 2px, transparent 7px)' }} />

            <p className="font-manrope text-sm leading-relaxed text-[#0B1B3A]/85">
              跨領域精選書籍，涵蓋茶文化、商業、科普、醫療保健、心理學與人文社科。
            </p>
          </div>

          <div className="mt-6">
            <p className="font-manrope text-[11px] leading-relaxed text-[#0B1B3A]/70">
              {brandIdentity.relationship}
            </p>
          </div>
        </div>

        {/* 右側：六個出版內容領域 */}
        <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
          {brandPublishingAreas.map((area, i) => (
            <div
              key={area.id}
              className="flex flex-col justify-between p-4 md:p-5 border"
              style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderColor: COLORS.line }}
            >
              <div className="flex items-center justify-between mb-3">
                <span
                  className="w-8 h-8 flex items-center justify-center rounded-md"
                  style={{ backgroundColor: 'rgba(79,179,217,0.18)', color: COLORS.cyan }}
                >
                  <i className={`${area.icon} text-base`}></i>
                </span>
                <span className="font-michroma text-[10px] text-white/35 tracking-widest">
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-white text-sm md:text-base mb-1.5">
                  {area.title}
                </h3>
                <p className="font-manrope text-[11px] md:text-xs text-white/55 leading-relaxed">
                  {area.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}